import {
  Anchor,
  atAnchor,
  LinearTransform,
  Offset,
  otherSide,
  Point,
  Rect,
  rectFromPoints
} from '@st/geom'
import type { PointerEvent } from 'react'
import { useCallback, useRef } from 'react'
import { useLinearTransform } from './use-linear-transform'

export type DragPointerEvent = {
  point: Point
  originalEvent: PointerEvent
}

export type MoveEvent =
  | {
      type: 'start'
      start: DragPointerEvent
    }
  | {
      type: 'move'
      start: DragPointerEvent
      current: DragPointerEvent
    }
  | {
      type: 'end'
      start: DragPointerEvent
      current: DragPointerEvent
    }

export type MoveEventHandler = (e: MoveEvent) => void
export type ResizeEventHandler = (e: ResizeEvent) => void
type ResizeEvent = MoveEvent & { anchor: Anchor }

type DragState = { status: 'idle' } | { status: 'dragging'; start: DragPointerEvent }

type MoveableOpts = {
  onMove: (e: MoveEvent) => void
  canvasClass: string
}
export function useMove({ onMove, canvasClass }: MoveableOpts) {
  const ref = useRef<HTMLDivElement | null>(null)
  const isDraggingRef = useRef<DragState>({ status: 'idle' })
  const transform = useLinearTransform()

  // To prevent over-reporting events, we throttle events with requestAnimationFrame
  // to match the framerate of the display
  const requestAnimationIdRef = useRef<number | null>(null)
  const startEventRef = useRef<DragPointerEvent | null>(null)
  const lastestMoveEventRef = useRef<PointerEvent | null>(null)

  const flushBufferedMoveEvent = useCallback(() => {
    requestAnimationIdRef.current = null
    const startEvent = startEventRef.current
    const latestMoveEvent = lastestMoveEventRef.current
    if (!startEvent || !latestMoveEvent) return

    onMove({
      type: 'move',
      start: startEvent,
      current: toDragPointerEvent(latestMoveEvent, transform, canvasClass)
    })
  }, [onMove, transform, canvasClass])

  const scheduleMoveEvent = useCallback(
    (e: PointerEvent) => {
      lastestMoveEventRef.current = e
      if (requestAnimationIdRef.current == null) {
        requestAnimationIdRef.current = requestAnimationFrame(flushBufferedMoveEvent)
      }
    },
    [flushBufferedMoveEvent]
  )

  const cancelRequestAnimationFrame = () => {
    if (requestAnimationIdRef.current != null) {
      cancelAnimationFrame(requestAnimationIdRef.current)
      requestAnimationIdRef.current = null
    }
  }

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const start = toDragPointerEvent(e, transform, canvasClass)
      startEventRef.current = start
      isDraggingRef.current = { status: 'dragging', start }
      ref.current?.setPointerCapture(e.pointerId)

      // Start events should be immediate (don’t wait for a frame)
      onMove({ type: 'start', start })
    },
    [onMove, transform, canvasClass]
  )

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (isDraggingRef.current.status !== 'dragging') return
      scheduleMoveEvent(e)
    },
    [scheduleMoveEvent]
  )

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const state = isDraggingRef.current
      isDraggingRef.current = { status: 'idle' }
      ref.current?.releasePointerCapture(e.pointerId)

      if (state.status !== 'dragging' || !startEventRef.current) {
        // Clean up any pending requestAnimationFrame
        cancelRequestAnimationFrame()
        lastestMoveEventRef.current = null
        startEventRef.current = null
        return
      }

      // Ensure final position is applied once (flush any pending move event)
      cancelRequestAnimationFrame()
      const endCurrent = toDragPointerEvent(e, transform, canvasClass)
      if (lastestMoveEventRef.current) {
        // One last move if the pointer actually moved since last frame
        onMove({ type: 'move', start: startEventRef.current, current: endCurrent })
      }

      onMove({ type: 'end', start: startEventRef.current, current: endCurrent })

      // Reset buffers
      lastestMoveEventRef.current = null
      startEventRef.current = null
    },
    [onMove, transform, canvasClass]
  )

  return { ref, onPointerDown, onPointerMove, onPointerUp }
}

type MoveResizeHook = {
  onMove: MoveEventHandler
  onResize: ResizeEventHandler
}

type UpdateEvent = {
  type: 'move' | 'resize'
  newBounds: Rect
  oldBounds: Rect
}

type CommitEvent = {
  type: 'move' | 'resize'
  newBounds: Rect
  oldBounds: Rect
}

type MoveResizeOpts = {
  initialBounds: Rect
  onUpdate: (event: UpdateEvent) => void
  onCommit: (event: CommitEvent) => void
}

/**
 * For editable elements expose onMove and onResize handlers.
 * Passing these into
 *
 * @param annot
 * @param send
 * @returns
 */
export function useUpdateBounds({
  initialBounds,
  onUpdate,
  onCommit
}: MoveResizeOpts): MoveResizeHook {
  const onMoveStartBounds = useRef<Rect | null>(null)

  const onMove: MoveEventHandler = useCallback(
    (e) => {
      switch (e.type) {
        case 'start':
          onMoveStartBounds.current = initialBounds
          break
        case 'move':
        case 'end':
          const baseRect = onMoveStartBounds.current!
          const newBounds = move(baseRect, {
            dx: e.current.point.x - e.start.point.x,
            dy: e.current.point.y - e.start.point.y
          })

          onUpdate({ type: 'move', newBounds, oldBounds: baseRect })

          if (e.type === 'end') {
            onCommit({ type: 'move', newBounds, oldBounds: baseRect })
          }
          break
      }
    },
    [initialBounds, onUpdate]
  )

  const onResizeStartBounds = useRef<Rect | null>(null)
  const onResize: ResizeEventHandler = useCallback(
    (e) => {
      switch (e.type) {
        case 'start':
          onResizeStartBounds.current = initialBounds
          break
        case 'move':
        case 'end':
          const baseRect = onResizeStartBounds.current!
          const startPoint = atAnchor(baseRect, e.anchor)
          const desiredPoint = move(startPoint, {
            dx: e.current.point.x - e.start.point.x,
            dy: e.current.point.y - e.start.point.y
          })
          const newBounds = moveControlPoint(baseRect, e.anchor, desiredPoint)

          onUpdate({ type: 'resize', newBounds, oldBounds: baseRect })

          if (e.type === 'end') {
            onCommit({ type: 'resize', newBounds, oldBounds: baseRect })
          }
          break
      }
    },
    [initialBounds, onUpdate]
  )

  return { onMove, onResize }
}

/**
 * Convert a raw PointerEvent to a special event which contains a
 * point transformed based on the zoom level.
 *
 * The element will get scaled by an amount but will not
 *
 * @param e
 * @param zoom
 * @param canvasClass
 * @returns
 */
export function toDragPointerEvent(
  e: PointerEvent,
  transform: LinearTransform,
  canvasClass: string
): DragPointerEvent {
  const target = e.target as HTMLElement

  // We want this coordinate system to be relative to the canvas element itself
  // If you are clicking on some empty space in the canvas, this will be e.target
  // If you are clicking on a nested element, for example a resize handle, you want to get the
  // closest ancestor
  const parent = target.classList.contains(canvasClass) ? target : target.closest(`.${canvasClass}`)
  const rect = parent!.getBoundingClientRect()

  const rawPointOnCanvas: Point = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }

  // The drawing canvas may have some transformations applied
  // This is implemented using the css transform(...) property (which are linear transformations)
  // For example it could might include scaling (zoom) and rotation
  //
  // However, mouse/pointer events are not transformed when applying the css transform
  // For example, if you are zoomed into a document at 200% and you click at (x: 50, y: 100)
  // you really want to know that this was (x: 25, y: 50) in the original document.
  //
  // To work around this, we can take the raw (x, y) coordinate and apply the inverse transform
  // to get the logical coordinate from the original canvas
  const invertedTransform = LinearTransform.invert(transform)
  const point = LinearTransform.apply(rawPointOnCanvas, invertedTransform)

  return { point, originalEvent: e }
}

/**
 * Given a rectangle, move a control point represented by an anchor to a new point keeping
 * the opposite points in place while maintaining the constraints of the rectangle.
 *
 * For example, if you take take {@link Anchor.topLeft} point and move it to a differnet place,
 * the x, y, width, and height would update with the bottom-right corner remains in place.
 *
 * @param rect
 * @param anchor
 * @param newPoint
 * @returns
 */
function moveControlPoint(rect: Rect, anchor: Anchor, newPoint: Point): Rect {
  return rectFromPoints(atAnchor(rect, otherSide(anchor)), newPoint)
}

export function move<T extends Point>(point: T, offset: Partial<Offset>): T {
  return {
    ...point,
    x: point.x + (offset.dx ?? 0),
    y: point.y + (offset.dy ?? 0)
  }
}
